/**
 * useItemPriceSeries (#2397) — the single twin-API interface point for the
 * Preisspiegel REAL own-price layer (APPS_LAYER convention: only the hook
 * touches twinService). Returns ``PriceSeries`` keyed by catalog item id.
 *
 * **#2646 moved the read.** This used to page raw ``price_observation`` derived
 * facts — one fact per priced invoice LINE — through
 * ``twinService.queryDerivedFacts``, 10 pages × 1000 rows, and aggregate them
 * client-side into a p50 median per day. Two things were wrong with that:
 *
 *  1. **It truncated.** WF's twin passed 33,000 price observations and
 *     erdbeeren alone is ~19–20k, so the walk dropped roughly half the item's
 *     history. #2573 made the truncation loud (the amber banner) without
 *     removing it, because the cap was never the real problem — the row grain
 *     was.
 *  2. **The day price carried a pack-size artefact.** A client median over
 *     per-line values gives a 500 g punnet the same vote as a 2 kg tray, which
 *     is the pinned REWE 8,80 / 5,20 case.
 *
 * It now reads ``GET /twin/preisspiegel/cells``: ``(day × series)`` cells that
 * UDB collapsed VOLUME-WEIGHTED at the source. The payload is O(days × series)
 * rather than O(invoice lines), so the ceiling moved from "how much did this
 * farm sell" to "how many days did it sell on". That is #2573's weighted read
 * finally reaching a user surface.
 *
 * **#2694 retired the catalog-wide read.** It used to be catalog-wide of
 * necessity: the lens cascade (``buildCatalogState``), the #2420
 * hide-empty-subtree rule and every tile's sparkline all needed "which items
 * have data" answered before the reader had drilled into anything, and only the
 * price read could answer it. ``GET /twin/preisspiegel/index`` answers it now in
 * a few dozen rows, so the price read is free to be narrow — and it should be,
 * because catalog-wide meant a ``?kat=saatgut`` visit downloading every berry
 * cell (~6.700 on WF) in order to draw eight Saatgut tiles.
 *
 * **The grain is WHAT IS ON SCREEN, deliberately** — not the tile, not the
 * tenant. Per tenant is what we are leaving; per tile would restore the fan-out
 * #2646 removed (one round trip per item on first paint, N of them in parallel).
 * In practice that is two callers, and never both at once:
 *
 *  - the tile GRID asks for the visible Kategorie's items. ``?item=`` is
 *    repeatable and capped at 500 server-side (``preisspiegel.py``
 *    ``_ITEM_FILTER_CAP``) while a Kategorie holds at most ~15, so one Kategorie
 *    visit is one round trip and a Kategorie the reader never opens is never
 *    paid for.
 *  - the drilled-in CHART asks for that one item (#2694 stage 3). This is not
 *    the per-tile fan-out: exactly one item can be drilled into, so it is one
 *    request, not N — and while it is on screen the grid's caller collapses its
 *    set to empty and issues nothing.
 *
 * At most one of the two is ever SUBSCRIBED. On the wire the claim is weaker: a
 * fetch already in flight when the key changes is not cancelled, so two
 * responses can still be in the air — but only one is ever read.
 *
 * A narrower caller does NOT get served from a wider caller's warm entry, even
 * though the wider one demonstrably holds its rows. See ``cellsForItems``: one
 * cache entry that answered to the catalog, a Kategorie and an item alike would
 * leave nothing able to say which scope a ``data_quality`` flag was about. The
 * extra round trip buys an unambiguous completeness claim, which is what the
 * #2652 bridge gate spends.
 *
 * The document-line → catalog-item crosswalk stays BACKEND-owned (#2394); this
 * hook never re-derives it.
 */
import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { twinService, type PreisspiegelCellsResponse } from '../services/twinService';
import { BENCHMARK_LEVELS, buildBenchmarkSeriesByItem } from '../pages/apps/preisspiegel/benchmarkCells';
import { buildOwnSeriesByItemFromCells, unmatchedCellItemIds } from '../pages/apps/preisspiegel/cells';
import type { PriceSeries } from '../pages/apps/preisspiegel/types';

/**
 * The request's item set, in canonical form: de-duplicated and sorted.
 *
 * This is what makes the cache key a function of the REQUEST rather than of the
 * order the caller happened to build its array in. Two Kategorien that genuinely
 * ask for the same items share one entry (correct — it is the same request);
 * one Kategorie re-rendered with a reordered list does not fragment into two.
 */
export function normaliseItemIds(itemIds: readonly string[]): string[] {
  return [...new Set(itemIds)].sort();
}

export const ownPriceKeys = {
  all: ['preisspiegel', 'own-price'] as const,
  /**
   * The NARROWED cells read, keyed by its exact item set.
   *
   * ⚠️ The key MUST carry the items. The catalog-wide read this replaced was
   * keyed ``[...all, 'cells']``; a narrowed read that reused that key would
   * share one cache entry with it and with every other Kategorie — serving one
   * Kategorie's prices as the whole catalog's, or the reverse, with nothing on
   * screen to give it away. The ``'items'`` discriminator plus the array make
   * that structurally impossible: no item set, INCLUDING the empty one, can
   * produce the retired key.
   *
   * ⚠️ It is also **deliberately not subset-aware**, and that is not an
   * oversight to optimise away. A drilled-in chart asking for one item while its
   * Kategorie's entry is warm WILL go to the network, even though that entry
   * holds the item's cells. Making a superset answer a subset would put one
   * entry behind three different scopes — catalog, Kategorie, item — and
   * ``data_quality`` travels with the entry, so a cap that fired while reading
   * fifteen items would silently qualify a chart drawn from one. There is
   * nothing on screen that could tell them apart. Pinned in
   * ``useItemPriceSeries.test.tsx`` as key inequality AND behaviourally.
   */
  cellsForItems: (itemIds: readonly string[]) =>
    [...ownPriceKeys.all, 'cells', 'items', normaliseItemIds(itemIds)] as const,
};

/** Whether what the chart is drawing is the whole of the tenant's price history. */
export interface OwnPriceDataQuality {
  /** The server's cell cap fired — observations are MISSING from the series below. */
  truncated: boolean;
  /**
   * Part of the population could not be read at all: the aggregate failed, or
   * rows carried payloads that would not parse. Distinct from ``truncated`` —
   * that is "we stopped early", this is "some of what we passed is unreadable".
   */
  degraded: boolean;
  /** Rows whose stored payload UDB could not parse. */
  malformed: number;
  /** ``(day × series)`` cells returned. */
  cellCount: number;
  /**
   * Invoice lines standing behind those cells (Σ each cell's ``n``) — the
   * number a support conversation wants, and the one the banner shows. Counting
   * cells there would silently understate the read by the fold factor.
   *
   * BOOKED only (#2777): a curated reference price is not an observation, and
   * padding this with the price book would overstate a read in the very sentence
   * that exists to admit it fell short.
   */
  observationCount: number;
  /**
   * HOF-19 — the plausibility marks could not be computed, so nothing in these
   * series is marked because it was UNCHECKED rather than because it passed.
   *
   * Read and surfaced rather than dropped, and that is the point: no cell is
   * missing when this fires, so nothing else on the page changes — which means
   * an unread flag would leave a failed check and a clean chart looking
   * identical. That ambiguity is the entire reason the server emits it.
   */
  marksUnavailable: boolean;
  /**
   * HOF-19 Phase B — the AMI benchmark read failed, or was truncated.
   *
   * Its own pair, not folded into ``degraded``: a benchmark that could not load
   * costs no own-price cell, so folding it in would blank the #2652 off-season
   * bridges over a complete price history. The chart uses these to say the
   * benchmark is missing rather than absent — "AMI has no data here" and "we
   * could not load AMI" are different sentences, and only one of them is a
   * statement about the market.
   */
  consumerBenchmarkDegraded: boolean;
  consumerBenchmarkTruncated: boolean;
  /**
   * HOF-248 — the same pair for the PUBLIC population. Separate because the two
   * reads fail separately, and because the failure sentence names its source:
   * telling a tenant with no AMI licence that "AMI MarktMonitor konnte nicht
   * geladen werden" is a claim about a panel it never asked for.
   */
  tradeBenchmarkDegraded: boolean;
  tradeBenchmarkTruncated: boolean;
  benchmarkLevels: readonly string[];
}

export interface UseItemPriceSeriesResult {
  /** Real tenant own-price series keyed by catalog item id (empty ⇒ no own data). */
  seriesByItem: Record<string, PriceSeries[]>;
  /**
   * HOF-19 Phase B — AMI benchmark series, keyed by catalog item id.
   *
   * A SEPARATE map from `seriesByItem`, deliberately. These are
   * *Endverbraucherpreise* observed in shops, not the farm's prices, and AC 8
   * requires that they never reach a statistic about what the farm sold. Keeping
   * them out of the own-price map makes that structural: a consumer computing an
   * average over `seriesByItem` cannot include one by forgetting a flag.
   */
  benchmarkSeriesByItem: Record<string, PriceSeries[]>;
  isLoading: boolean;
  error: Error | null;
  dataQuality: OwnPriceDataQuality;
  /**
   * The read produced no usable answer at all: it threw AND we hold nothing for
   * this item set.
   *
   * **Not** the same as ``error !== null``. React Query keeps the last good
   * ``data`` when a REFETCH fails, and this query is ``staleTime``-bounded, so a
   * single flaky response over a warm cache would otherwise raise "prices could
   * not be loaded" directly above a complete, correct grid — and flip every
   * genuinely-empty tile from "Keine Daten" to "Preis nicht verfügbar". Holding
   * stale rows is not the same as holding nothing. (The same discrimination
   * ``usePriceIndex`` makes, and more load-bearing here: per-Kategorie means
   * many more reads over a session, so a flaky one is likelier.)
   */
  unavailable: boolean;
}

const EMPTY_QUALITY: OwnPriceDataQuality = {
  truncated: false,
  degraded: false,
  malformed: 0,
  cellCount: 0,
  observationCount: 0,
  marksUnavailable: false,
  consumerBenchmarkDegraded: false,
  consumerBenchmarkTruncated: false,
  tradeBenchmarkDegraded: false,
  tradeBenchmarkTruncated: false,
  benchmarkLevels: BENCHMARK_LEVELS,
};

/**
 * What an empty item set resolves to WITHOUT reaching the network.
 *
 * RESOLVED, never rejected: a rejection would set ``error`` on the empty entry,
 * and whichever caller is currently parked there would report ``unavailable``
 * and raise a failure banner about a read nobody asked for.
 */
const EMPTY_CELLS: PreisspiegelCellsResponse = {
  cells: [],
  estimate_cells: [],
  group_by: [],
  side: null,
  beispieldaten: false,
  data_quality: { truncated: false, degraded: false, malformed: 0, cell_count: 0 },
};

/**
 * Own-price series for a NAMED SET of catalog items — in practice the visible
 * Kategorie's items while the tile grid is up, or the single drilled item while
 * the chart is, both resolved from the index read.
 *
 * An EMPTY set does not fetch. That is not an optimisation: an empty ``items``
 * array appends no ``item`` param, so the request would be byte-identical to the
 * retired CATALOG-WIDE read. An empty Kategorie — or the tick before the index
 * answers — would silently restore exactly what #2694 removed, and it would look
 * fine, because the response is a superset of what the page needs.
 */
/**
 * The whole of the cell READ's derivation, as a pure function of the response.
 *
 * Extracted for HOF-271, and for the same reason as
 * ``buildPriceIndexResult``: ``GET /twin/preisspiegel/tiles`` carries this
 * route's body verbatim as the other half of its payload, and the tiles hook has
 * to turn it into exactly this result. A second copy would be free to drift, and
 * the drift would be invisible — both copies produce a plausible chart.
 *
 * ``isLoading`` is the caller's, because the two callers mean different things by
 * it: here it is one query's flag, and in the tiles hook it is the state of a
 * read that also answers the shell.
 */
export function buildItemPriceSeriesResult(
  data: PreisspiegelCellsResponse | undefined,
  { isLoading, error }: { isLoading: boolean; error: Error | null },
): UseItemPriceSeriesResult {
  const cells = data?.cells;
  // #2777 — the CURATED slice, read beside the booked one. Skipping it is not a
  // smaller version of this feature: the index half re-admits an estimate-only
  // item to the page (the #2694 Saatgut tiles), and a price read that ignores
  // `estimate_cells` lands it on a tile with no series, rendering "Keine Daten"
  // directly under a shell that just said the item has a price. Each series is
  // stamped `estimate`, so nothing downstream can average it into a booked one.
  const estimateCells = data?.estimate_cells;
  const seriesByItem = buildOwnSeriesByItemFromCells(cells ?? [], estimateCells ?? []);

  // HOF-19 Phase B — the AMI panel, built into its OWN map (see the result
  // type). Absent on a server that predates the benchmark, which reads as "no
  // benchmark" and renders the pre-HOF-19 chart.
  const benchmarkSeriesByItem = buildBenchmarkSeriesByItem(data?.benchmark_cells ?? []);

  const dq = data?.data_quality;
  const dataQuality: OwnPriceDataQuality = !dq
    ? EMPTY_QUALITY
    : {
        truncated: dq.truncated,
        degraded: dq.degraded,
        malformed: dq.malformed,
        cellCount: dq.cell_count,
        // Absent reads as "fine" — a server that predates the mark computed none
        // that could have failed.
        marksUnavailable: Boolean(dq.marks_unavailable),
        // Absent reads as "fine", like every additive key here: a server that
        // predates the benchmark ran no benchmark read that could have failed.
        consumerBenchmarkDegraded: Boolean(dq.consumer_benchmark_degraded),
        consumerBenchmarkTruncated: Boolean(dq.consumer_benchmark_truncated),
        tradeBenchmarkDegraded: Boolean(dq.trade_benchmark_degraded),
        tradeBenchmarkTruncated: Boolean(dq.trade_benchmark_truncated),
        // Which lines the server LOOKED for. Falls back to this build's own
        // list rather than to empty: an older server that sends no such key
        // still draws both lines, and an empty list would silence every
        // "no index for this product" note it should have produced.
        benchmarkLevels: Array.isArray(dq.benchmark_levels) ? dq.benchmark_levels : BENCHMARK_LEVELS,
        observationCount: (cells ?? []).reduce((total, c) => total + (Number.isFinite(c.n) ? c.n : 0), 0),
      };

  return {
    seriesByItem,
    benchmarkSeriesByItem,
    isLoading,
    error,
    dataQuality,
    // Gated on holding NO data, not merely on `error` — see the field docstring.
    unavailable: error !== null && data === undefined,
  };
}

/**
 * #2420 observability: a cell whose catalog_item_id matches no catalog item
 * would vanish silently from every view (its byItem key is never read).
 * Surface that FE-mirror-vs-server drift as a structured warning instead — plus
 * a breadcrumb for an incomplete read, which the banner tells the farmer and
 * this tells whoever is reading their session.
 *
 * A hook rather than part of {@link buildItemPriceSeriesResult}: it is a side
 * effect, and that builder now has two callers.
 */
export function useCellDriftWarnings(data: PreisspiegelCellsResponse | undefined): void {
  const cells = data?.cells;
  useEffect(() => {
    if (!cells?.length) return;
    const unmatched = unmatchedCellItemIds(cells);
    if (unmatched.length > 0) {
      console.warn(
        `[preisspiegel] ${unmatched.length} price cell catalog_item_id(s) match no catalog item ` +
          `(mirror-vs-server drift): ${unmatched.join(', ')}`,
      );
    }
  }, [cells]);

  const dq = data?.data_quality;
  useEffect(() => {
    if (!dq) return;
    if (dq.truncated || dq.degraded) {
      console.warn(
        `[preisspiegel] price cell read came back incomplete (truncated=${dq.truncated}, ` +
          `degraded=${dq.degraded}, malformed=${dq.malformed}); the series shown are INCOMPLETE`,
      );
    }
  }, [dq]);
}

export function useItemPriceSeries(itemIds: readonly string[]): UseItemPriceSeriesResult {

  const items = useMemo(() => normaliseItemIds(itemIds), [itemIds]);

  const query = useQuery({
    queryKey: ownPriceKeys.cellsForItems(items),
    // The empty set is short-circuited HERE as well as by `enabled`, and the
    // redundancy is the point. `enabled` is an observer-level flag: it stops the
    // hook fetching and it makes `refetchQueries`/`invalidateQueries` skip the
    // entry, but `fetchQuery`/`prefetchQuery`/`ensureQueryData` bypass it
    // entirely. Since #2694 stage 3 the empty key is mounted in EVERY page state
    // (the grid parks on it while the chart is up, the chart while the grid is),
    // so one imperative fetch landing on it would put the retired catalog-wide
    // request back on the wire — and it would look fine, because the response is
    // a superset of what the page needs. Structural beats conventional here.
    queryFn: () => (items.length === 0 ? Promise.resolve(EMPTY_CELLS) : twinService.getPreisspiegelCells({ items })),
    // The fast path: no request is even attempted. The guard above is the
    // invariant; this is the optimisation.
    enabled: items.length > 0,
    // HOF-93 — the SERIES layer is allowed to be stale; the index is not.
    //
    // Prices here are invoice-derived: they change when a Gutschrift is ingested,
    // which is hours or days, never seconds. At 30 s a reader who drilled into a
    // tile and came back paid for the whole Kategorie again on a timescale
    // nothing could have changed in — which is what made returning to the grid
    // feel like a fresh page load rather than a step back.
    //
    // `usePriceIndex` deliberately stays at 30 s and is NOT changed here. It is
    // the layer that answers "does this item have data at all", so it is the one
    // that has to notice a newly-ingested item; the curve under an already-known
    // headline is the part that can wait.
    staleTime: 300_000,
    // What makes drill-in-and-back free rather than merely fast: the entry has
    // to still BE there when the reader returns, and React Query's default gc is
    // 5 min. A Kategorie's cells are a few hundred KB and bounded by the
    // catalog, so holding them for half an hour costs little and removes the
    // round trip entirely.
    gcTime: 1_800_000,
    // Deliberately NO `placeholderData: keepPreviousData`. Carrying the previous
    // Kategorie's cells across a key change would leave `isLoading` false while
    // the new Kategorie's prices are still in flight, so its tiles would render
    // "Keine Daten" — a statement about items the read has not covered yet, made
    // with the confidence of one that has. A skeleton on Kategorie switch is the
    // honest answer: the previous read says nothing about these items.
  });

  const error = (query.error as Error | undefined) ?? null;
  const result = useMemo(
    () => buildItemPriceSeriesResult(query.data, { isLoading: query.isLoading, error }),
    [query.data, query.isLoading, error],
  );
  useCellDriftWarnings(query.data);
  return result;
}
